import DairyService from '@src/api/services/dairy';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { StateEl } from '../style';
import { FormFields } from '@src/components/FormFields';
import { GlobalEl } from '@src/styles/app';
import DairyQuery from '@src/QueryHooks/DairyQuery';
import notify from '~/components/Notify';
import { useMutation } from 'react-query';
import Drawer from '~/components/Drawer';
import { useAuth } from '~/routes/components/AuthProvider';
import { MenuItem, Tab } from 'semantic-ui-react';
import styled from 'styled-components';
import { StyledTabPane } from '~/pages/Dairy/Settings/General/style';

interface Props {
  isVisible: boolean;
  handleHiddenDrawer: () => void;
  isLoading?: boolean;
  fetchData?: () => void,
  idTransportationArea?: any
}

const StyledMenuItem = styled(MenuItem)`
 .error {
  color: red;
 }
`

const CreateEditTransportationArea: React.FunctionComponent<Props> = (props) => {
  const { isVisible, handleHiddenDrawer, idTransportationArea } = props;
  const { t } = useTranslation();
  const { systemData: { languages } } = useAuth()
  const { handleSubmit, control, reset, formState } = useForm({
    mode: 'onSubmit'
  });

  useEffect(() => {
    if (idTransportationArea) {
      editTransportationArea(idTransportationArea);
    } else {
      reset({
        languages: getLangs()
      });
    }
  }, [isVisible, idTransportationArea]);

  function getLangs() {
    return languages.map(language => ({
      id: language.id,
      name: '',
      abbreviation: ''
    }))
  }

  function getParsedLanguages(statusLangs) {
    languages.map((language) => {
      const index = statusLangs.findIndex(languageText => languageText.id === language.id)
      if (index < 0) {
        statusLangs.push({
          id: language.id,
          name: '',
          abbreviation: '',
          order: language.order
        })
      } else {
        statusLangs[index].order = language.order
      }
    })
    statusLangs.sort((a, b) => a.order - b.order)
    statusLangs?.forEach(lang => delete lang.order)
    return statusLangs
  }

  const onSuccess = () => {
    notify(t('Successfully created transportation area'), 'success');
    props.fetchData()
  };

  const onTransportationAreaSuccess = (response) => {
    response.data.languages = getParsedLanguages(response.data.languages)
    reset(response.data)
  }

  const { mutateAsync: storeTransportationArea } =
    DairyQuery.storeTransportationArea(onSuccess);

  const { mutate: updateTransportationArea } = useMutation(
    'transportation-areas-update',
    DairyService.updateTransportationArea,
    {
      onSuccess: () => {
        notify(t('Successfully updated transportation area'), 'success');
        props.fetchData()
      }
    }
  );

  const { isLoading: isTransportationAreaLoading, mutateAsync: editTransportationArea } =
    DairyQuery.editTransportationArea(onTransportationAreaSuccess);

  const onSubmit = (data) => {
    if (!idTransportationArea) {
      storeTransportationArea({ ...data });
    } else {
      updateTransportationArea({ id: idTransportationArea, data })
    }
    handleHiddenDrawer();
  };

  function errorTab() {
    //@ts-ignore
    return formState.errors?.languages?.map((lang, index) => index)?.filter(item => typeof (item) === 'number')
  }

  function getMenuItem(label, index) {
    const errors = errorTab() || []

    return <StyledMenuItem
      key={label}
      className='tab-item'
    >
      <div className={`${errors.includes(index) ? 'error' : ''}`}>{label}</div>
    </StyledMenuItem>
  }

  function renderLanguageFields() {
    return <Tab
      menu={{ secondary: true, pointing: true }}
      renderActiveOnly={false}
      panes={
        languages.map((language) => {
          const indexLanguage = languages.findIndex(languageIndex => languageIndex['id'] === language.id)
          return {
            //@ts-ignore
            menuItem: getMenuItem(language['label'], indexLanguage),
            pane: <StyledTabPane key={language.id}>
              <FormFields.Input
                control={control}
                name={`languages[${indexLanguage}].name`}
                label={t('Transportation area name')}
                placeholder={t('Transportation area name')}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter Transportation area name')
                  }
                }}
              />
              <FormFields.Input
                control={control}
                label={t('Transportation area abbreviation')}
                placeholder={t('Transportation area abbreviation')}
                name={`languages[${indexLanguage}].abbreviation`}
                rules={{
                  required: {
                    value: true,
                    message: t('Please enter Transportation area abbreviation')
                  },
                  maxLength: {
                    value: 20,
                    message: t('Max. 20 characters')
                  }
                }}
              />
            </StyledTabPane>
          }
        })}
    />
  }

  const renderContent = () => {
    return (
      <>
        {renderLanguageFields()}
        <StateEl.DrawerFooter>
          <GlobalEl.PrimaryButton
            disabled={Object.keys(formState.dirtyFields).length === 0}
          >{idTransportationArea ? t('Update') : t('Add')}</GlobalEl.PrimaryButton>
        </StateEl.DrawerFooter>
      </>
    );
  };

  return <Drawer
    isVisible={isVisible}
    handleSubmit={handleSubmit(onSubmit)}
    isBusy={isTransportationAreaLoading}
    header={idTransportationArea ? t('Edit transportation area') : t('Create transportation area')}
    closeOnEscape
    onHide={() => {
      reset();
      handleHiddenDrawer();
    }}
    content={renderContent()}
  />
};

export default CreateEditTransportationArea;
